import { useRef, useState } from 'react';
import { useFormContext, useController } from 'react-hook-form';
import { cx } from '@emotion/css';

import { mergeRefs, callAllFn, _noop } from '@julofinance/web-helpers/dist/fn';

import PhotoImg from 'assets/photo.png';

import {
  IdentityCamera,
  FieldGroup,
  CameraRef,
  useSnackbar,
} from '@new-components/elements';

import useUploadImage from './usecase/use-upload-image';
import { ApplicationCameraProps } from './types';
import { applicationCameraCx, fieldGroupCx } from './styles';

const ApplicationCamera = (props: ApplicationCameraProps) => {
  const {
    label,
    name,
    required,
    rules,
    onCameraResultOK = _noop,
    cameraFacing,
    readonly,
    ...resProps
  } = props;

  const [preview, setPreview] = useState('');

  const cameraRef = useRef<CameraRef>(null);

  const { onOpen } = useSnackbar();
  const { control, setValue } = useFormContext();
  const {
    fieldState: { error, invalid },
    field: {
      ref: controlRef,
      onChange: controllerOnChange,
      value: controllerValue,
      ...resFieldProps
    },
  } = useController({
    name,
    control,
    rules: {
      ...(required && { required: 'This field is required' }),
      ...(rules && rules),
    },
  });

  const { uploadImage, isUploadingImage } = useUploadImage({
    name,
    onError: ({ payload }) => {
      const errorMessage =
        (payload?.errors as string[])?.[0] ?? 'Opss.. Something went wrong';

      setValue(name, '');
      onOpen({ message: errorMessage, severity: 'error' });
    },
    onSuccess: () => {
      onCameraResultOK(preview);
      controllerOnChange(preview);
      cameraRef.current?.closeCamera();
    },
  });

  return (
    <FieldGroup
      name={name}
      label={label}
      invalid={invalid}
      error={error}
      className={fieldGroupCx}
    >
      <div className={cx('camera-field', applicationCameraCx)}>
        <div
          className={cx('image-wrapper', {
            preview: Boolean(controllerValue),
            readonly,
          })}
        >
          <img
            src={controllerValue || PhotoImg}
            onClick={() => cameraRef.current?.openCamera()}
            alt='camera-image'
          />
        </div>
      </div>
      <IdentityCamera
        {...resProps}
        cameraFacing={cameraFacing}
        onCameraResultOK={callAllFn(uploadImage, setPreview)}
        ref={mergeRefs(cameraRef, controlRef)}
        isLoading={isUploadingImage}
        {...resFieldProps}
      />
    </FieldGroup>
  );
};

export default ApplicationCamera;
